"use client"
import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { PlusIcon, ArrowLeft, UsersIcon, SearchIcon } from "lucide-react"
import { User, columns } from "./columns"
import { DataTable } from "@/components/data-table"
import UserService from "@/api/users.service";
import { useToast } from '@/hooks/use-toast'
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

async function getData(toast: any, businessId: number): Promise<User[]> {
  // Fetch data from your API here.
  try {
    const response = await UserService.getAllUsers(businessId);
    if (response.data.result === 'invalid') {
      window.location.href = '/logout';
      return [];
    } else if (response.data.result === 'success') {
      return response.data.users.map((user: any) => ({
        id: user.id,
        publicId: user.publicId,
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        emailAddress: user.emailAddress,
        phoneNumber: user.phoneNumber,
        gender: user.gender,
        city: user.city,
        physicalAddress: user.physicalAddress,
        country: user.country,
        password: user.password,
        isAdmin: user.isAdmin,
        isActive: user.isActive,
        status: user.status,
        activationCode: user.activationCode,
        timezone: user.timezone,
        dateUpdated: user.dateUpdated,
        dateCreated: user.dateCreated,
      }));
    } else {
      toast({
        title: "Error",
        description: response.data.message || "Failed to fetch users",
        variant: "destructive",
      });
      return [];
    }
  } catch (error) {
    toast({
      title: "Connection Error",
      description: "Could not fetch user data. Please try again.",
      variant: "destructive",
    });
    return [];
  }
}

export default function Users() {
  const [data, setData] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      const result = await getData(toast, selectedBusiness.id);
      setIsLoading(false);
      setData(result);
    };
    fetchData();
  }, [toast, selectedBusiness]);

  const filteredData = data.filter(user => 
    user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.emailAddress?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Card className="border-none shadow-sm">
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => window.history.back()} 
              className="text-slate-600 hover:text-slate-900"
            > 
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <CardTitle className="text-2xl font-semibold text-slate-900 flex items-center">
              <UsersIcon className="w-5 h-5 mr-2 text-blue-600" />
              Users
            </CardTitle>
          </div>
          <Button 
            onClick={() => window.location.href=`/users/create`} 
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transition-all"
          >
            <PlusIcon className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="mb-6 relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
          <Input
            placeholder="Search users by name or email..."
            className="pl-10 border-slate-200 focus:border-blue-500 transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-100 overflow-hidden">
            <DataTable 
              columns={columns} 
              data={filteredData} 
              section="Users" 
              isLoading={isLoading} 
            />
          </div>
        )}
        
        <div className="mt-4 text-sm text-slate-500 text-right">
          {!isLoading && `Showing ${filteredData.length} of ${data.length} users`}
        </div>
      </CardContent>
    </Card>
  );
}
